import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ServiceWorkerContext } from './ServiceWorkerContext';

export function ServiceWorkerProvider({ children }) {
    const [ pendingUpdate, setPendingUpdate ] = useState(null);
    const listenersRef = useRef(new Map());

    const emit = useCallback((type, detail) => {
        const handlers = listenersRef.current.get(type);
        if (handlers) {
            handlers.forEach((handler) => handler(detail));
        }
    }, []);

    const subscribe = useCallback((type, handler) => {
        if (!listenersRef.current.has(type)) {
            listenersRef.current.set(type, new Set());
        }
        listenersRef.current.get(type).add(handler);
        return () => {
            listenersRef.current.get(type)?.delete(handler);
        };
    }, []);

    useEffect(() => {
        const handleUpdate = (event) => {
            setPendingUpdate(event.detail);
            emit('update', event.detail);
        };
        window.addEventListener('pwa-update', handleUpdate);

        const handleControllerChange = () => emit('controllerchange');
        navigator.serviceWorker?.addEventListener('controllerchange', handleControllerChange);

        return () => {
            window.removeEventListener('pwa-update', handleUpdate);
            navigator.serviceWorker?.removeEventListener('controllerchange', handleControllerChange);
        };
    }, [ emit ]);

    const applyUpdate = useCallback(() => {
        const waiting = pendingUpdate?.waiting;
        if (!waiting) {
            return;
        }
        if (!navigator.serviceWorker?.controller) {
            waiting.postMessage({ type: 'SKIP_WAITING' });
            window.location.reload();
            return;
        }
        navigator.serviceWorker.addEventListener('controllerchange', () => {
            window.location.reload();
        }, { once: true });
        waiting.postMessage({ type: 'SKIP_WAITING' });
    }, [ pendingUpdate ]);

    const value = useMemo(() => ({
        pendingUpdate,
        hasPendingUpdate: !!pendingUpdate,
        subscribe,
        applyUpdate,
    }), [ pendingUpdate, subscribe, applyUpdate ]);

    return (
        <ServiceWorkerContext.Provider value={ value }>
            { children }
        </ServiceWorkerContext.Provider>
    );
}
